import React from "react";
import BehaviourBarGraph from "../BehaviourBarGraph.jsx";
import Graph from "./TRIM";
import { formatMass, formatNumber } from "@signolteam/shared/units-of-measure";
import AppUtils from "@src/utils/appUtils";

//metatdata format
let defaultMetadata = {
  trim: 1.15, //in meters
  trim_ideal: 0.2, //in meters
  trim_perc_limit: 15, // max % increase in fuel usage from optimal to succeed
  fuel_consumption_actual_trim: 26000, // in kg /!\ need to be below threshold to show success on manager module /!\
  fuel_consumption_perfect_trim: 25000, // in kg
  // OT in meters:
  perfect_trim_m: 1, // in meters
  ot_threshold_upper_m: 1.25, // max meters over or under ideal trim to succeed
  ot_threshold_lower_m: 0.75, // min meters over or under ideal trim to succeed
};

export const behaviourMetadataFields = Object.keys(defaultMetadata);

export default function graph({
  bvrColour,
  unit = "MT",
  metadata = defaultMetadata,
  industry,
  customerCode,
  missingDataMsg,
  isExample,
}) {
  const featureCheck = AppUtils.isFeatureAvailableForCustomer;
  const trim = Number(metadata["trim"] || metadata["trim_m"]);
  const idealTrim = Number(metadata["trim_ideal"] || 0);
  const limitPerc = Number(
    metadata["trim_perc_limit"] || metadata["trim_threshold_percent"],
  );
  const actual = Number(metadata["fuel_consumption_actual_trim"]);
  const ideal = Number(metadata["fuel_consumption_perfect_trim"]);
  const perfectTrimM = Number(metadata["perfect_trim_m"]);
  const lowerLimitMeters = Number(metadata["ot_threshold_lower_m"]);
  const upperLimitMeters = Number(metadata["ot_threshold_upper_m"]);

  if (
    (!featureCheck("OTInMeters", customerCode) &&
      (isNaN(trim) ||
        isNaN(idealTrim) ||
        isNaN(limitPerc) ||
        isNaN(actual) ||
        isNaN(ideal))) ||
    (featureCheck("OTInMeters", customerCode) &&
      (isNaN(trim) ||
        isNaN(perfectTrimM) ||
        isNaN(lowerLimitMeters) ||
        isNaN(upperLimitMeters)))
  ) {
    return (
      <div className="relative text-center my-6 mx-auto text-2xl fill-current">
        {missingDataMsg}
      </div>
    );
  }

  if (featureCheck("OTInMeters", customerCode)) {
    const formattedPerfectTrim = formatNumber(perfectTrimM, {
      unit: "m",
      precision: 2,
    }).formattedValue;

    const originLabel = `Optimal trim${isExample ? "" : ` (${formattedPerfectTrim})`}`;
    const axisLabel = "Deviation from optimal trim (m)";

    // Draft advisory temporary solution
    const minDraft = Number(metadata["ot_min_draft_aft_m"]);
    const draft = Number(metadata["draft_aft_m"]);
    const hasRequiredMetadata = !isNaN(minDraft) && !isNaN(draft);
    const showDraftAdvisory =
      hasRequiredMetadata && Math.abs(draft - minDraft) < 0.25;
    const draftAdvisory = (
      <div className="w-full h-full mb-1 px-1 md:px-1">
        <div
          className={`h-full overflow-x-auto custom-scroll md:text-base rounded-xl p-4`}
          style={{ backgroundColor: bvrColour.codes["100"] }}
        >
          <p className="text-left text-black font-bold">
            The vessel was within 0.25m of the minimum aft draft.
          </p>
          <p className="text-left text-black">
            Remember, fully immersing the propeller prevents uneven thrust and
            bending of the shaft. This greatly improves propeller efficiency.
          </p>
        </div>
      </div>
    );

    return (
      <div>
        <Graph
          trim={trim}
          perfectTrimM={perfectTrimM}
          lowerLimitMeters={lowerLimitMeters}
          upperLimitMeters={upperLimitMeters}
          bvrColour={bvrColour}
          originLabel={originLabel}
          axisLabel={axisLabel}
          isExample={isExample}
        />
        {showDraftAdvisory && draftAdvisory}
      </div>
    );
  } else {
    const threshold = Number(
      Number(ideal) + (Number(ideal) / 100) * Number(limitPerc),
    );

    const data = [
      {
        name: `Fuel burn with optimal trim (${idealTrim}m)`,
        mobileName: `Label 1`,
        beforeThreshold: ideal,
        afterThreshold: 0,
        underThresholdColour:
          actual > threshold ? bvrColour.codes["200"] : bvrColour.codes["500"],
        overThresholdColour: bvrColour.codes["900"],
      },
      {
        name: `Fuel burn with actual trim (${trim}m)`,
        mobileName: "Label 2",
        beforeThreshold: actual > threshold ? threshold : actual,
        afterThreshold: actual > threshold ? actual - threshold : 0,
        underThresholdColour:
          actual > threshold ? bvrColour.codes["200"] : bvrColour.codes["500"],
        overThresholdColour: bvrColour.codes["900"],
      },
    ];

    const domainMaxActual = 0.1 * Number(actual) + Number(actual);
    const domainMaxThreshold = 0.1 * Number(threshold) + Number(threshold);
    const domainMin = 0;
    const domainMax =
      actual > threshold
        ? Math.ceil(Number(domainMaxActual))
        : Math.ceil(Number(domainMaxThreshold));
    const domain = [domainMin, domainMax];

    const mobileMaxActual = 0.05 * Number(actual) + Number(actual);
    const mobileMaxThreshold = 0.05 * Number(threshold) + Number(threshold);
    const mobileDomainMax =
      actual > threshold
        ? Math.ceil(Number(mobileMaxActual))
        : Math.ceil(Number(mobileMaxThreshold));
    const mobileDomain = [domainMin, mobileDomainMax];

    const thresholdLineColour = bvrColour.codes["900"];
    return (
      <BehaviourBarGraph
        data={data}
        threshold={threshold}
        thresholdLabel={"Ship-specific threshold"}
        mobileDomain={mobileDomain}
        domain={domain}
        refLineColour={thresholdLineColour}
        success={actual < threshold}
        unitOfMeasure={` ${unit}`}
        axisLabel={`Fuel consumption (${unit})`}
        hideYLine={true}
        hideNum={true}
        barLabelFormatter={(value) =>
          `${formatMass(industry, value, 0, { boundaryValues: [0, threshold] }).formattedValue}`
        }
        isExample={isExample}
      />
    );
  }
}
